'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Users,
  ClipboardList,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Settings,
  Wrench,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-clients', label: 'Klijenti', href: '/client-management', icon: <Users size={20} /> },
  { id: 'nav-interventions', label: 'Intervencije', href: '/intervention-log', icon: <ClipboardList size={20} />, badge: 1 },
  { id: 'nav-reports', label: 'Izveštaji', href: '/reports', icon: <BarChart2 size={20} /> },
];

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="relative flex flex-col h-full border-r transition-all duration-300 ease-in-out"
      style={{
        width: collapsed ? '64px' : '240px',
        minWidth: collapsed ? '64px' : '240px',
        backgroundColor: 'hsl(var(--card))',
        borderColor: 'hsl(var(--border))',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-3 px-4 py-5 border-b"
        style={{ borderColor: 'hsl(var(--border))', minHeight: '72px' }}
      >
        <AppLogo size={32} />
        {!collapsed && (
          <span
            className="font-bold text-lg tracking-tight truncate"
            style={{ color: 'hsl(var(--primary))' }}
          >
            ServisPro
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <p
            className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider"
            style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.08em' }}
          >
            Glavni meni
          </p>
        )}
        {NAV_ITEMS.map((item) => {
          const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.id}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`sidebar-nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center' : ''}`}
            >
              <span className="flex-shrink-0">{item.icon}</span>
              {!collapsed && (
                <span className="flex-1 truncate">{item.label}</span>
              )}
              {!collapsed && item.badge && item.badge > 0 ? (
                <span
                  className="ml-auto flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: 'hsl(var(--accent))', color: 'white' }}
                >
                  {item.badge}
                </span>
              ) : null}
            </Link>
          );
        })}

        {!collapsed && (
          <div className="pt-4">
            <p
              className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider"
              style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.08em' }}
            >
              Sistem
            </p>
          </div>
        )}
        <Link
          href="#"
          title={collapsed ? 'Podešavanja' : undefined}
          className={`sidebar-nav-item ${collapsed ? 'justify-center' : ''}`}
        >
          <span className="flex-shrink-0"><Settings size={20} /></span>
          {!collapsed && <span className="flex-1 truncate">Podešavanja</span>}
        </Link>
      </nav>

      {/* User */}
      {!collapsed && (
        <div
          className="px-4 py-4 border-t"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
              style={{ backgroundColor: 'hsl(var(--accent))' }}
            >
              T
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'hsl(var(--foreground))' }}>
                Tehničar
              </p>
              <p className="text-xs truncate" style={{ color: 'hsl(var(--muted-foreground))' }}>
                <Wrench size={10} className="inline mr-1" />ServisPro
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full border flex items-center justify-center transition-all duration-150 hover:scale-110 z-10"
        style={{
          backgroundColor: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
          color: 'hsl(var(--muted-foreground))',
          boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
        }}
        aria-label={collapsed ? 'Proširi' : 'Skupi'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}